import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiLink,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { mdiChevronLeft, mdiPlus } from "@mdi/js";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { AddTitleDrawer } from "./AddTitleDrawer/AddTitleDrawer";
import { useManageTitleViewModel } from "./useManageTitleViewModel";

export const ManageTitle = () => {
  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const { hasPermission } = usePermissions();

  const { titles, getTitles, createTitle, deleteTitle, stateManager } =
    useManageTitleViewModel();

  const { classes, theme } = useStyles();

  useEffect(() => {
    getTitles();
  }, []);

  const hasEditPermission = hasPermission(PERMISSIONS.TITLE.UPDATE);

  const titleFieldSet = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      flex: 1,
      renderCell: hasEditPermission
        ? (p) => (
            <MiLink
              key={p.row.id}
              title={p.value}
              to={`/settings/user-management/titles/${p.row.id}`}
            />
          )
        : null,
    },
    {
      field: "description",
      headerName: "Description",
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
  ];

  const onCreate = (data) => {
    createTitle(data, () => {
      setOpenAddDrawer(false);
      getTitles();
    });
  };
  const onDelete = () => {
    deleteTitle(
      selectedItem.id,
      { modifiedOn: selectedItem.modifiedOn },
      () => {
        setIsOpenDeleteModal(false);
        setSelectedItem(null);
        getTitles();
      }
    );
  };
  const onDeleteCancel = () => {
    setIsOpenDeleteModal(false);
    setSelectedItem(null);
  };

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.DELETE,
      onClick: () => {
        setSelectedItem(row);
        setIsOpenDeleteModal(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          size={1}
          color={theme.palette.red.main}
        />
      ),
    },
  ];

  return (
    <Box className={classes.root}>
      <div className={classes.contentContainer}>
        <MiDetailPageHeader
          title="Titles"
          titleIcon={mdiChevronLeft}
          loading={stateManager.isBusy()}
          actionComponent={
            hasPermission(PERMISSIONS.TITLE.CREATE) && (
              <MiButton
                title="Add Title"
                variant={BUTTON_VARIANTS.CONTAINED}
                color={BUTTON_COLORS.PRIMARY}
                startIcon={{
                  path: mdiPlus,
                  iconSize: 0.8,
                }}
                onClick={() => setOpenAddDrawer(true)}
              />
            )
          }
        />
        <Box className={classes.container}>
          <MiList
            columns={titleFieldSet}
            data={titles}
            paginationMode={PAGINATION.CLIENT}
            loading={stateManager.isBusy()}
            getRowActions={
              hasPermission(PERMISSIONS.TITLE.DELETE) && getRowActions
            }
            data-testid="users-table"
          />
        </Box>
        <AddTitleDrawer
          open={openAddDrawer}
          onClose={() => setOpenAddDrawer(false)}
          onSubmit={onCreate}
          loading={stateManager.isBusy()}
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          description={`Are you sure you want to delete title: ${selectedItem?.name}?`}
          open={isOpenDeleteModal}
          handlePositive={onDelete}
          handleNegative={onDeleteCancel}
          className={classes.modal}
          textPositive={BUTTON_LABELS.DELETE}
        />
      </div>
    </Box>
  );
};
const useStyles = makeStyles()((theme) => ({
  root: {
    height: "inherit",
    display: "flex",
  },
  container: {
    height: "inherit",
    overflowY: "scroll",
    margin: 5,
    borderRadius: 10,
    padding: 5,
    backgroundColor: theme.palette.common.white,
  },
  contentContainer: { display: "flex", flexDirection: "column", width: "100%" },
  modal: {
    minWidth: 400,
  },
}));
